import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';

// Utility function to handle Firestore timestamps
function parseFirestoreTimestamp(timestamp) {
  if (!timestamp) return null;
  
  // Handle server timestamp objects
  if (timestamp._methodName === 'serverTimestamp') {
    return new Date(); // Use current time as fallback
  }
  
  // Handle Firestore timestamp objects
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // Handle legacy _seconds format
  if (timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  }
  
  // Handle regular Date objects or strings
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  return null;
}

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        console.log('[OrderConfirmation] Starting order fetch for orderId:', orderId);
        
        if (!auth.currentUser) {
          console.log('[OrderConfirmation] No authenticated user, redirecting to signin');
          navigate('/signin');
          return;
        }

        console.log('[OrderConfirmation] User authenticated:', auth.currentUser.uid);

        // Use the new multi-vendor order system
        const { getParentOrderWithDetails, getParentOrderByPaymentIntent } = await import('../utils/multiVendorOrderApi');
        
        let orderData;
        
        // Check if this is a payment intent ID (starts with 'pi_')
        if (orderId.startsWith('pi_')) {
          console.log('[OrderConfirmation] Order ID is a payment intent ID, searching by payment intent...');
          try {
            orderData = await getParentOrderByPaymentIntent(orderId);
            console.log('[OrderConfirmation] Payment intent search result:', orderData ? 'Found' : 'Not found');
          } catch (paymentIntentError) {
            console.error('[OrderConfirmation] Payment intent search failed:', paymentIntentError);
            throw new Error(`Payment intent search failed: ${paymentIntentError.message}`);
          }
        } else {
          console.log('[OrderConfirmation] Order ID is a Firestore document ID, fetching directly...');
          try {
            orderData = await getParentOrderWithDetails(orderId);
            console.log('[OrderConfirmation] Direct fetch result:', orderData ? 'Found' : 'Not found');
          } catch (directFetchError) {
            console.error('[OrderConfirmation] Direct fetch failed:', directFetchError);
            throw new Error(`Direct order fetch failed: ${directFetchError.message}`);
          }
        }
        
        if (!orderData) {
          throw new Error('Order not found in database');
        }
        
        console.log('[OrderConfirmation] Order data loaded successfully:', {
          orderId: orderData.parentOrder?.id,
          status: orderData.parentOrder?.status,
          total: orderData.parentOrder?.total
        });
        
        setOrder(orderData);
      } catch (err) {
        console.error('[OrderConfirmation] Error fetching order:', err);
        console.error('[OrderConfirmation] Error details:', {
          message: err.message,
          stack: err.stack,
          orderId: orderId
        });
        setError(`Failed to load order details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C97B53] mx-auto mb-4"></div>
          <p className="text-gray-600 text-xs">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-xs font-bold text-gray-900 mb-2">Order Not Found</h1>
          <p className="text-gray-600 mb-6 text-xs">{error}</p>
          <button
            onClick={() => navigate('/customer-dashboard')}
            className="bg-[#C97B53] text-white px-6 py-3 rounded-lg hover:bg-[#B86A42] transition-colors text-xs"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8 font-montserrat">
      <div className="max-w-2xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <img 
              src="/Vesta_Logo.svg" 
              alt="Vesta Logo" 
              className="w-24 h-24 mx-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-600 text-xs">Thank you for your order. We'll send you updates via email.</p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xs font-semibold text-gray-900 mb-4">Order Details</h2>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Order ID:</span>
              <span className="font-mono text-xs">{order.parentOrder?.id || order.id}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Order Date:</span>
              <span className="text-xs">
                {(() => {
                  const orderDate = parseFirestoreTimestamp(order.parentOrder?.createdAt);
                  return orderDate ? orderDate.toLocaleDateString() : 'N/A';
                })()}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Status:</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                {order.parentOrder?.status || order.status}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Total Amount:</span>
              <span className="font-bold text-xs">${Number(order.parentOrder?.totals?.total || order.total || 0).toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Subtotal:</span>
              <span className="text-xs">${Number(order.parentOrder?.totals?.subtotal || 0).toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Tax:</span>
              <span className="text-xs">${Number(order.parentOrder?.totals?.tax || 0).toFixed(2)}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs">Number of Sellers:</span>
              <span className="font-medium text-xs">{order.merchantOrders?.length || 1}</span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h3 className="text-xs font-semibold text-gray-900 mb-4">Order Items</h3>
          
          <div className="space-y-6">
            {order.merchantOrders?.map((merchantOrder, merchantIndex) => (
              <div key={merchantOrder.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                <h4 className="font-semibold text-gray-800 mb-3 text-xs">
                  Seller {merchantIndex + 1} - Order #{merchantOrder.id.slice(-6).toUpperCase()}
                </h4>
                
                <div className="space-y-3">
                  {merchantOrder.items?.map((item, itemIndex) => (
                    <div key={item.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500 text-xs">🍽️</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-xs">{item.productSnapshot?.name || `Item ${itemIndex + 1}`}</p>
                          {item.productSnapshot?.variant && Object.keys(item.productSnapshot.variant).length > 0 && (
                            <p className="text-xs text-gray-500">
                              {Object.entries(item.productSnapshot.variant).map(([key, value]) => `${key}: ${value}`).join(', ')}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                          <p className="text-xs text-blue-600">
                            Fulfillment: {new Date(item.serviceDate + 'T00:00:00').toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            Time: {item.windowStartLocal} - {item.windowEndLocal}
                          </p>
                        </div>
                      </div>
                      <span className="font-medium text-xs">
                        ${(Number(item.unitPrice) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Fallback for old order structure */}
            {!order.merchantOrders && order.items?.map((item, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-gray-500 text-xs">🍽️</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-xs">{item.name || `Item ${index + 1}`}</p>
                    <p className="text-xs text-gray-500">Qty: {item.quantity || item.qty || 1}</p>
                    {item.fulfillmentDay && (
                      <p className="text-xs text-blue-600">
                        Fulfillment: {new Date(item.fulfillmentDay + 'T00:00:00').toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <span className="font-medium text-xs">
                  ${(Number(item.price || 0) * (item.quantity || item.qty || 1)).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Information */}
        {(order.parentOrder?.billingDetails || order.billingDetails) && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h3 className="text-xs font-semibold text-gray-900 mb-4">Delivery Information</h3>
            
            <div className="space-y-2">
              <p className="text-xs"><span className="font-medium">Name:</span> {order.parentOrder?.billingDetails?.name || order.billingDetails?.name}</p>
              <p className="text-xs"><span className="font-medium">Email:</span> {order.parentOrder?.billingDetails?.email || order.billingDetails?.email}</p>
              {(order.parentOrder?.billingDetails?.address || order.billingDetails?.address) && (
                <p className="text-xs"><span className="font-medium">Address:</span> {order.parentOrder?.billingDetails?.address?.line1 || order.billingDetails?.address?.line1}</p>
              )}
              {order.parentOrder?.shippingAddress && (
                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p className="font-medium text-xs text-gray-700 mb-1">Shipping Address:</p>
                  <p className="text-xs">{order.parentOrder.shippingAddress.name}</p>
                  <p className="text-xs">{order.parentOrder.shippingAddress.street}</p>
                  <p className="text-xs">{order.parentOrder.shippingAddress.city}, {order.parentOrder.shippingAddress.zip}</p>
                  {order.parentOrder.shippingAddress.phone && (
                    <p className="text-xs">{order.parentOrder.shippingAddress.phone}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-xl p-6 mb-6">
          <h3 className="text-xs font-semibold text-blue-900 mb-3">What's Next?</h3>
          <ul className="space-y-2 text-blue-800 text-xs">
            <li className="flex items-start">
              <span className="mr-2">📧</span>
              <span className="text-xs">You'll receive an order confirmation email shortly</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">👨‍🍳</span>
              <span className="text-xs">Our food creators are preparing your order</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">🚚</span>
              <span className="text-xs">We'll notify you when your order is ready for pickup or delivery</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/multi-vendor-order-history')}
            className="flex-1 bg-gradient-to-r from-[#C97B53] to-[#E89B5A] text-white py-3 px-6 rounded-lg hover:from-[#B86A42] hover:to-[#D88A4A] transition-colors font-medium text-xs"
          >
            View All Orders
          </button>
          
          <button
            onClick={() => navigate('/fooddiscovery')}
            className="flex-1 bg-white text-transparent bg-clip-text bg-gradient-to-r from-[#C97B53] to-[#E89B5A] py-3 px-6 rounded-lg border-2 border-[#C97B53] hover:border-[#B86A42] transition-colors font-medium text-xs"
          >
            Order More Food
          </button>
        </div>

        {/* Support Information */}
        <div className="text-center mt-8 text-xs text-gray-500">
          <p className="text-xs">Need help? Contact us at support@vesta.com</p>
          <p className="text-xs">Order ID: {order.parentOrder?.id || order.id}</p>
        </div>
      </div>
    </div>
  );
}
